"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Field,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  INPUT_MONO,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  CONTINENTS,
  COUNTRIES,
  type Continent,
  type Country,
  drawCountries,
  filterCountries,
} from "@/lib/tool-kit/random-fun/countries";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_COUNTRY_GENERATOR_STRINGS = {
  continentsLabel: "Continents to draw from",
  continentAF: "Africa",
  continentAS: "Asia",
  continentEU: "Europe",
  continentNA: "North America",
  continentOC: "Oceania",
  continentSA: "South America",
  alsoIn: "also {continent}",
  includeObservers: "Include the two observer states (Holy See, Palestine)",
  countLabel: "How many (1–195)",
  uniqueLabel: "No repeats",
  quizLabel: "Quiz mode: hide the capital until I ask",
  generate: "Pick random countries",
  results: "Your countries",
  placeholder: "Press the button to draw countries.",
  announce: "Picked {count}: {first}.",
  colCountry: "Country",
  colCapital: "Capital",
  colContinent: "Continent",
  colCode: "ISO code",
  reveal: "Show capital",
  observerTag: "observer",
  statPool: "Countries in the pool",
  statDrawn: "Countries drawn",
  copyAll: "Copy as table",
  copied: "Copied!",
  problemEmpty: "Tick at least one continent to draw from.",
  problemCount: "Enter how many countries you want, from 1 to 195.",
  problemTooMany:
    "Only {pool} {countries} fit your choices, so {count} different ones is too many. Lower the number, tick more continents or allow repeats.",
  countriesWord: "one: country|other: countries",
} as const;

type Strings = typeof RANDOM_COUNTRY_GENERATOR_STRINGS;

const CONTINENT_KEY: Record<Continent, keyof Strings> = {
  AF: "continentAF",
  AS: "continentAS",
  EU: "continentEU",
  NA: "continentNA",
  OC: "continentOC",
  SA: "continentSA",
};

const MAX_DRAW = COUNTRIES.length;

function CountryRow({
  country,
  strings,
  quiz,
}: {
  country: Country;
  strings: Strings;
  quiz: boolean;
}) {
  const continent = strings[CONTINENT_KEY[country.continent]];
  return (
    <tr>
      <td className={TD}>
        {country.name}
        {country.observer ? (
          <span className="ml-2 text-muted-foreground text-xs">
            {strings.observerTag}
          </span>
        ) : null}
      </td>
      <td className={TD}>
        {quiz ? (
          <details>
            <summary className="cursor-pointer text-primary text-xs">
              {strings.reveal}
            </summary>
            {country.capital}
          </details>
        ) : (
          country.capital
        )}
      </td>
      <td className={TD}>
        {continent}
        {country.alsoIn ? (
          <span className="block text-muted-foreground text-xs">
            {t(strings.alsoIn, {
              continent: strings[CONTINENT_KEY[country.alsoIn]],
            })}
          </span>
        ) : null}
      </td>
      <td className={`${TD} font-mono`}>{country.code}</td>
    </tr>
  );
}

export default function RandomCountryGeneratorWidget() {
  const strings = useToolUi(RANDOM_COUNTRY_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [continents, setContinents] =
    useState<readonly Continent[]>(CONTINENTS);
  const [observers, setObservers] = useState(false);
  const [countText, setCountText] = useState("5");
  const [unique, setUnique] = useState(true);
  const [quiz, setQuiz] = useState(false);
  const [drawn, setDrawn] = useState<Country[]>([]);
  const [shownQuiz, setShownQuiz] = useState(false);
  const [error, setError] = useState("");
  const [announcement, setAnnouncement] = useState("");

  const pool = filterCountries({ continents, includeObservers: observers });

  function toggleContinent(continent: Continent, on: boolean) {
    setContinents(
      CONTINENTS.filter((c) => (c === continent ? on : continents.includes(c)))
    );
  }

  function generate() {
    const count = parseIntegerInput(countText);
    if (pool.length === 0) {
      setError(strings.problemEmpty);
      return;
    }
    if (count === null || count < 1 || count > MAX_DRAW) {
      setError(strings.problemCount);
      return;
    }
    if (unique && count > pool.length) {
      setError(
        t(strings.problemTooMany, {
          pool: fmt(pool.length),
          countries: tPlural(strings.countriesWord, locale, pool.length),
          count: fmt(count),
        })
      );
      return;
    }
    const result = drawCountries(pool, count, unique);
    setError("");
    setDrawn(result);
    setShownQuiz(quiz);
    setAnnouncement(
      t(strings.announce, {
        count: fmt(result.length),
        first: result[0]?.name ?? "",
      })
    );
  }

  const tsv = drawn
    .map((country) =>
      [
        country.name,
        country.capital,
        strings[CONTINENT_KEY[country.continent]],
        country.code,
      ].join("\t")
    )
    .join("\n");

  return (
    <div className="space-y-6">
      <fieldset className="space-y-3">
        <legend className="font-medium text-foreground text-sm">
          {strings.continentsLabel}
        </legend>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {CONTINENTS.map((continent) => (
            <Checkbox
              checked={continents.includes(continent)}
              key={continent}
              label={strings[CONTINENT_KEY[continent]]}
              onChange={(on) => toggleContinent(continent, on)}
            />
          ))}
        </div>
        <Checkbox
          checked={observers}
          label={strings.includeObservers}
          onChange={setObservers}
        />
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field id="rcg2-count" label={strings.countLabel}>
          <input
            className={INPUT_MONO}
            id="rcg2-count"
            inputMode="numeric"
            onChange={(event) => setCountText(event.target.value)}
            type="text"
            value={countText}
          />
        </Field>
        <div className="flex flex-col justify-end gap-2 sm:col-span-2">
          <Checkbox
            checked={unique}
            label={strings.uniqueLabel}
            onChange={setUnique}
          />
          <Checkbox
            checked={quiz}
            label={strings.quizLabel}
            onChange={setQuiz}
          />
        </div>
      </div>

      <StatGrid>
        <StatCard label={strings.statPool} value={fmt(pool.length)} />
        <StatCard label={strings.statDrawn} value={fmt(drawn.length)} />
      </StatGrid>

      <button className={BUTTON_PRIMARY} onClick={generate} type="button">
        {strings.generate}
      </button>

      {error ? <ErrorNotice>{error}</ErrorNotice> : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>
        {drawn.length === 0 ? (
          <Notice>{strings.placeholder}</Notice>
        ) : (
          <>
            <div className="max-h-[32rem] overflow-auto rounded-lg border border-border bg-card">
              <table className={TABLE}>
                <thead>
                  <tr>
                    <th className={TH} scope="col">
                      {strings.colCountry}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colCapital}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colContinent}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colCode}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {drawn.map((country, index) => (
                    <CountryRow
                      country={country}
                      key={`${index}-${country.code}`}
                      quiz={shownQuiz}
                      strings={strings}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyAll}
              text={tsv}
            />
          </>
        )}
      </section>
    </div>
  );
}
